import api from './api'
import {
  isBeyondSyncWindow,
  isReplayablePunch,
  listMutations,
  listUnacknowledgedMutations,
  pruneAcknowledgedMutations,
  toPunchSyncEvent,
  updateMutation,
  type QueuedMutation,
  type QueuedMutationStatus,
} from './offlineWorkerQueue'

/**
 * Reconnect replay for the offline worker queue.
 *
 * Every replay goes through the EXISTING worker APIs and the existing backend
 * idempotency behavior. Nothing here bypasses geofence evaluation or offline
 * punch review: an offline punch is submitted to `/api/workers/me/punch-events/sync`
 * and the backend files it for supervisor review exactly as it does for the
 * Android client.
 */

const MAX_BATCH = 25
const MAX_ATTEMPTS = 6

export interface OfflineSyncResult {
  attempted: number
  acknowledged: number
  rejected: number
  failed: number
  skippedReason?: 'offline' | 'nothing_pending' | 'unsupported'
}

export interface OfflineQueueSummary {
  pending: number
  syncing: number
  failed: number
  requiresReview: number
  synced: number
  total: number
}

export function summarizeQueue(rows: QueuedMutation[]): OfflineQueueSummary {
  const summary: OfflineQueueSummary = { pending: 0, syncing: 0, failed: 0, requiresReview: 0, synced: 0, total: rows.length }
  for (const row of rows) {
    if (row.status === 'pending') summary.pending += 1
    else if (row.status === 'syncing') summary.syncing += 1
    else if (row.status === 'failed') summary.failed += 1
    else if (row.status === 'requires_review') summary.requiresReview += 1
    else if (row.status === 'synced') summary.synced += 1
  }
  return summary
}

export async function readQueueSummary(): Promise<OfflineQueueSummary> {
  return summarizeQueue(await listMutations())
}

/** Failed actions the worker still needs to see and act on. */
export async function readFailedMutations(): Promise<QueuedMutation[]> {
  return (await listMutations()).filter((row) => row.status === 'failed')
}

export interface PunchSyncResponse {
  accepted?: Array<{ clientEventId: string; serverEventId?: string }>
  duplicates?: Array<{ clientEventId: string; serverEventId?: string }>
  rejected?: Array<{ clientEventId: string; message?: string }>
}

export type MutationPatch = Partial<QueuedMutation> & { status: QueuedMutationStatus }

/**
 * Map a batch sync response onto local status changes.
 *
 * Pure so the rules a worker's pay depends on can be asserted directly:
 *  - accepted AND duplicate both mean the backend owns the punch now, and
 *    because the backend files offline punches for supervisor review, the
 *    worker is told `requires_review` — never a bare "done";
 *  - rejected is surfaced with the backend's own reason;
 *  - anything the server did not mention stays queued with an incremented
 *    attempt count, rather than silently disappearing.
 */
export function resolvePunchSyncOutcomes(
  rows: QueuedMutation[],
  data: PunchSyncResponse
): Array<{ id: string; patch: MutationPatch; category: 'acknowledged' | 'rejected' | 'unanswered' }> {
  const outcomes: Array<{ id: string; patch: MutationPatch; category: 'acknowledged' | 'rejected' | 'unanswered' }> = []
  const answered = new Set<string>()

  for (const entry of [...(data.accepted || []), ...(data.duplicates || [])]) {
    answered.add(entry.clientEventId)
    outcomes.push({
      id: entry.clientEventId,
      category: 'acknowledged',
      patch: { status: 'requires_review', serverEventId: entry.serverEventId || null, lastError: null },
    })
  }
  for (const entry of data.rejected || []) {
    answered.add(entry.clientEventId)
    outcomes.push({
      id: entry.clientEventId,
      category: 'rejected',
      patch: { status: 'failed', lastError: entry.message || 'The server rejected this offline punch.' },
    })
  }
  for (const row of rows) {
    if (answered.has(row.id)) continue
    outcomes.push({
      id: row.id,
      category: 'unanswered',
      patch: { status: 'pending', attempts: row.attempts + 1 },
    })
  }
  return outcomes
}

/**
 * A 4xx (other than 429) will never succeed on retry, so it is surfaced to the
 * worker instead of being retried until the attempt budget runs out.
 */
export function resolveMutationFailure(
  row: QueuedMutation,
  input: { status?: number; message?: string }
): { patch: MutationPatch; permanent: boolean } {
  const attempts = row.attempts + 1
  const permanent = typeof input.status === 'number'
    && input.status >= 400 && input.status < 500 && input.status !== 429
  return {
    permanent,
    patch: {
      status: permanent || attempts >= MAX_ATTEMPTS ? 'failed' : 'pending',
      attempts,
      lastError: input.message || 'Sync failed.',
    },
  }
}

async function replayPunchBatch(rows: QueuedMutation[], result: OfflineSyncResult): Promise<void> {
  const events = rows.map(toPunchSyncEvent)
  await Promise.all(rows.map((row) => updateMutation(row.id, { status: 'syncing' })))
  try {
    const response = await api.post('/api/workers/me/punch-events/sync', { events })
    const data = (response.data?.data || {}) as PunchSyncResponse
    for (const outcome of resolvePunchSyncOutcomes(rows, data)) {
      await updateMutation(outcome.id, outcome.patch)
      if (outcome.category === 'acknowledged') result.acknowledged += 1
      else if (outcome.category === 'rejected') result.rejected += 1
      else result.failed += 1
    }
  } catch (error) {
    const message = (error as any)?.response?.data?.message || (error as Error)?.message || 'Sync failed.'
    for (const row of rows) {
      await updateMutation(row.id, resolveMutationFailure(row, { message }).patch)
      result.failed += 1
    }
  }
}

async function replayAttendanceReport(row: QueuedMutation, result: OfflineSyncResult): Promise<void> {
  await updateMutation(row.id, { status: 'syncing' })
  try {
    if (row.kind === 'on_the_way') {
      await api.post(`/api/workers/me/assignments/${row.assignmentId}/on-the-way`,
        typeof row.latitude === 'number' && typeof row.longitude === 'number'
          ? { lat: row.latitude, lon: row.longitude, accuracyMeters: row.accuracyMeters ?? undefined }
          : {})
    } else {
      await api.post(`/api/workers/me/assignments/${row.assignmentId}/attendance-report`, {
        reportType: row.action,
        notes: row.notes || '',
      })
    }
    await updateMutation(row.id, { status: 'synced', lastError: null })
    result.acknowledged += 1
  } catch (error) {
    const failure = resolveMutationFailure(row, {
      status: (error as any)?.response?.status,
      message: (error as any)?.response?.data?.message || (error as Error)?.message,
    })
    await updateMutation(row.id, failure.patch)
    if (failure.permanent) result.rejected += 1
    else result.failed += 1
  }
}

/*
 * Replay can be triggered from several places at once: the `online` event, a
 * visibility change, a Service Worker Background Sync wake-up, and an explicit
 * "Sync now" tap. The backend's idempotency handling already prevents a
 * duplicate punch, but overlapping replays would still send the same events
 * twice and race each other's local status writes, so a single in-flight
 * replay is shared by all callers.
 */
let inFlightReplay: Promise<OfflineSyncResult> | null = null

/**
 * Replay everything the backend has not acknowledged. Safe to call repeatedly —
 * concurrent callers join the in-flight pass, and the backend's existing
 * idempotency handling resolves anything that still arrives twice.
 */
export function replayOfflineQueue(options: { online?: boolean } = {}): Promise<OfflineSyncResult> {
  if (inFlightReplay) return inFlightReplay
  inFlightReplay = runReplay(options).finally(() => { inFlightReplay = null })
  return inFlightReplay
}

async function runReplay(options: { online?: boolean } = {}): Promise<OfflineSyncResult> {
  const result: OfflineSyncResult = { attempted: 0, acknowledged: 0, rejected: 0, failed: 0 }
  const online = options.online ?? (typeof navigator === 'undefined' ? true : navigator.onLine !== false)
  if (!online) return { ...result, skippedReason: 'offline' }

  const unacknowledged = await listUnacknowledgedMutations()

  // An action past the backend's replay window can never be accepted. Surface it
  // to the worker as failed with a reason they can act on, rather than leaving it
  // queued forever pretending it will still be sent.
  for (const row of unacknowledged) {
    if (row.status === 'failed' || !isBeyondSyncWindow(row)) continue
    await updateMutation(row.id, {
      status: 'failed',
      failureCode: 'expired_beyond_sync_window',
      lastError: null,
    })
    result.rejected += 1
  }

  const pending = unacknowledged.filter((row) =>
    row.attempts < MAX_ATTEMPTS && !isBeyondSyncWindow(row))
  if (!pending.length) {
    await pruneAcknowledgedMutations()
    return { ...result, skippedReason: 'nothing_pending' }
  }
  result.attempted = pending.length

  const punches = pending.filter(isReplayablePunch)
  for (let index = 0; index < punches.length; index += MAX_BATCH) {
    await replayPunchBatch(punches.slice(index, index + MAX_BATCH), result)
  }
  for (const row of pending) {
    if (isReplayablePunch(row)) continue
    if (row.kind === 'punch') {
      // A punch with no captured coordinates cannot use the offline sync
      // contract. It is surfaced for the worker to retake rather than dropped.
      await updateMutation(row.id, {
        status: 'failed',
        failureCode: 'missing_location',
        lastError: null,
      })
      result.rejected += 1
      continue
    }
    await replayAttendanceReport(row, result)
  }
  await pruneAcknowledgedMutations()
  return result
}
